/**
 * Domain Mappings Page
 *
 * Lists all domain mappings for a tenant with create, edit, and delete actions.
 * Capability-gated to users with admin:tenants capability.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Environment } from "@tatvex/types";
import {
  ArrowLeft,
  Globe,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import {
  DomainMappingForm,
  type DomainMappingFormValues,
} from "../../../components/admin/DomainMappingForm";
import { useAuthStore } from "../../../store/authStore";

// ============================================================================
// Types
// ============================================================================

/** Domain mapping entity from API */
interface DomainMapping {
  readonly id: string;
  readonly tenantId: string;
  readonly environment: Environment;
  readonly domain: string;
  readonly isPrimary: boolean;
  readonly createdAt: string;
}

/** Domain list API response */
interface DomainListData {
  readonly domains: readonly DomainMapping[];
  readonly total: number;
}

/** UI mode for the page */
type PageMode = "list" | "create" | "edit";

// ============================================================================
// Constants
// ============================================================================

const ENVIRONMENT_LABELS: Record<Environment, string> = {
  dev: "Development",
  staging: "Staging",
  prod: "Production",
};

const ENVIRONMENT_COLORS: Record<Environment, string> = {
  dev: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  staging: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  prod: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
};

// ============================================================================
// Main Component
// ============================================================================

export function DomainMappingsPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  if (!hasCapability("admin:tenants")) {
    return <Navigate to="/studio" replace />;
  }

  return <DomainMappingsContent />;
}

function DomainMappingsContent(): ReactElement {
  const { tenantId } = useParams<{ tenantId: string }>();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<PageMode>("list");
  const [editingDomain, setEditingDomain] = useState<DomainMapping | null>(null);
  const [deletingDomain, setDeletingDomain] = useState<DomainMapping | null>(null);

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["domains", tenantId],
    queryFn: async () => {
      const response = await apiRequest<DomainListData>(`/api/v1/tenants/${tenantId}/domains`);
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: Boolean(tenantId),
  });

  // ==========================================================================
  // Mutations
  // ==========================================================================

  const createMutation = useMutation({
    mutationFn: async (values: DomainMappingFormValues) => {
      const response = await apiRequest<DomainMapping>(`/api/v1/tenants/${tenantId}/domains`, {
        method: "POST",
        body: values,
      });
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["domains", tenantId] });
      setMode("list");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      domainId,
      values,
    }: {
      domainId: string;
      values: DomainMappingFormValues;
    }) => {
      const response = await apiRequest<DomainMapping>(
        `/api/v1/tenants/${tenantId}/domains/${domainId}`,
        {
          method: "PUT",
          body: values,
        }
      );
      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["domains", tenantId] });
      setMode("list");
      setEditingDomain(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (domainId: string) => {
      const response = await apiRequest<{ success: boolean }>(
        `/api/v1/tenants/${tenantId}/domains/${domainId}`,
        { method: "DELETE" }
      );
      if (!response.success) {
        throw new Error(response.error.message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["domains", tenantId] });
      setDeletingDomain(null);
    },
  });

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleEdit = useCallback((domain: DomainMapping): void => {
    setEditingDomain(domain);
    setMode("edit");
  }, []);

  const handleCreate = useCallback(
    (values: DomainMappingFormValues): void => {
      createMutation.mutate(values);
    },
    [createMutation]
  );

  const handleUpdate = useCallback(
    (values: DomainMappingFormValues): void => {
      if (!editingDomain) return;
      updateMutation.mutate({ domainId: editingDomain.id, values });
    },
    [editingDomain, updateMutation]
  );

  const handleCancelForm = useCallback((): void => {
    setMode("list");
    setEditingDomain(null);
    createMutation.reset();
    updateMutation.reset();
  }, [createMutation, updateMutation]);

  const handleConfirmDelete = useCallback((): void => {
    if (!deletingDomain) return;
    deleteMutation.mutate(deletingDomain.id);
  }, [deletingDomain, deleteMutation]);

  // ==========================================================================
  // Render
  // ==========================================================================

  if (isLoading) {
    return <LoadingState tenantId={tenantId} />;
  }

  if (isError) {
    return (
      <ErrorState
        tenantId={tenantId}
        message={error instanceof Error ? error.message : "Failed to load domain mappings"}
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6">
        <Link
          to={`/studio/admin/tenants/${tenantId}`}
          className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tenant
        </Link>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Globe className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Domain Mappings</h1>
              <p className="mt-1 text-foreground-muted">Manage custom domains for this tenant</p>
            </div>
          </div>

          {mode === "list" && (
            <button onClick={() => setMode("create")} className="btn btn-primary">
              <Plus className="mr-2 h-4 w-4" />
              Add Domain
            </button>
          )}
        </div>
      </div>

      {/* Create/Edit Form */}
      {mode === "create" && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">Add Domain Mapping</h2>
          <DomainMappingForm
            onSubmit={handleCreate}
            onCancel={handleCancelForm}
            isSubmitting={createMutation.isPending}
            {...(createMutation.error instanceof Error
              ? { error: createMutation.error.message }
              : {})}
          />
        </div>
      )}

      {mode === "edit" && editingDomain && (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-4">Edit Domain Mapping</h2>
          <DomainMappingForm
            initialValues={{
              domain: editingDomain.domain,
              environment: editingDomain.environment,
              isPrimary: editingDomain.isPrimary,
            }}
            onSubmit={handleUpdate}
            onCancel={handleCancelForm}
            isSubmitting={updateMutation.isPending}
            {...(updateMutation.error instanceof Error
              ? { error: updateMutation.error.message }
              : {})}
            isEdit
          />
        </div>
      )}

      {/* Domain Mappings Table */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Domains ({data?.total ?? 0})</h2>

        {!data?.domains.length ? (
          <EmptyState />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="pb-3 pr-4 font-medium text-foreground-muted">Domain</th>
                  <th className="pb-3 pr-4 font-medium text-foreground-muted">Environment</th>
                  <th className="pb-3 pr-4 font-medium text-foreground-muted">Primary</th>
                  <th className="pb-3 pr-4 font-medium text-foreground-muted">Created At</th>
                  <th className="pb-3 font-medium text-foreground-muted">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.domains.map((mapping) => (
                  <DomainRow
                    key={mapping.id}
                    mapping={mapping}
                    onEdit={handleEdit}
                    onDelete={setDeletingDomain}
                    isFormOpen={mode !== "list"}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deletingDomain && (
        <DeleteConfirmationDialog
          domain={deletingDomain.domain}
          isLoading={deleteMutation.isPending}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeletingDomain(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Domain Row Component
// ============================================================================

interface DomainRowProps {
  readonly mapping: DomainMapping;
  readonly onEdit: (mapping: DomainMapping) => void;
  readonly onDelete: (mapping: DomainMapping) => void;
  readonly isFormOpen: boolean;
}

function DomainRow({ mapping, onEdit, onDelete, isFormOpen }: DomainRowProps): ReactElement {
  return (
    <tr className="border-b border-border last:border-0">
      <td className="py-3 pr-4">
        <span className="font-mono text-sm">{mapping.domain}</span>
      </td>
      <td className="py-3 pr-4">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            ENVIRONMENT_COLORS[mapping.environment]
          }`}
        >
          {ENVIRONMENT_LABELS[mapping.environment]}
        </span>
      </td>
      <td className="py-3 pr-4">
        {mapping.isPrimary ? (
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            Primary
          </span>
        ) : (
          <span className="text-foreground-muted">-</span>
        )}
      </td>
      <td className="py-3 pr-4 text-foreground-muted">
        {new Date(mapping.createdAt).toLocaleDateString()}
      </td>
      <td className="py-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(mapping)}
            disabled={isFormOpen}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-foreground-muted hover:bg-background hover:text-foreground disabled:opacity-50"
            title="Edit domain mapping"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
          <button
            onClick={() => onDelete(mapping)}
            disabled={isFormOpen}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-error hover:bg-error/10 disabled:opacity-50"
            title="Delete domain mapping"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// Delete Confirmation Dialog
// ============================================================================

interface DeleteConfirmationDialogProps {
  readonly domain: string;
  readonly isLoading: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function DeleteConfirmationDialog({
  domain,
  isLoading,
  onConfirm,
  onCancel,
}: DeleteConfirmationDialogProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-error/10 text-error">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold">Delete Domain Mapping</h2>
        </div>

        <p className="text-foreground-muted mb-2">
          Are you sure you want to delete the domain mapping for:
        </p>
        <p className="font-mono text-sm font-semibold mb-6">{domain}</p>
        <p className="text-sm text-foreground-muted mb-6">
          This will immediately stop routing traffic from this domain to the tenant. This action
          cannot be undone.
        </p>

        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={isLoading} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={isLoading} className="btn btn-error">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Domain"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// State Components
// ============================================================================

function EmptyState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Globe className="h-8 w-8" />
      <p className="mt-2">No domain mappings configured</p>
      <p className="text-xs mt-1">Add a domain to route traffic to this tenant.</p>
    </div>
  );
}

interface LoadingStateProps {
  readonly tenantId: string | undefined;
}

function LoadingState({ tenantId }: LoadingStateProps): ReactElement {
  return (
    <div className="p-6">
      <Link
        to={`/studio/admin/tenants/${tenantId}`}
        className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Tenant
      </Link>
      <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="mt-4">Loading domain mappings...</p>
      </div>
    </div>
  );
}

interface ErrorStateProps {
  readonly tenantId: string | undefined;
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ tenantId, message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="p-6">
      <Link
        to={`/studio/admin/tenants/${tenantId}`}
        className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Tenant
      </Link>
      <div className="flex flex-col items-center justify-center py-12">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
          <AlertCircle className="h-6 w-6 text-error" />
        </div>
        <p className="mt-4 text-foreground-muted">{message}</p>
        <button onClick={onRetry} className="btn btn-secondary mt-4">
          <RefreshCw className="mr-2 h-4 w-4" />
          Try again
        </button>
      </div>
    </div>
  );
}
